import { graphicImageryPolicyValues } from '@borradh-workspace/labels';
import { z } from 'zod';
import { graphicLogoPlacementSchema } from '../graphic-logo.js';
import { campaignPackageReviewSchema } from './campaign-package.js';
/**
 * The shared card envelope union.
 *
 * WHY THIS FILE EXISTS.
 *
 * Today every tool result's `presentation` field is typed as
 * `PresentationPayload` in `apps/api/src/assistant/tool-factory/types.ts`,
 * which ends in the escape hatch
 *
 *     | (Record<string, unknown> & { type: string })
 *
 * That escape hatch is what allows any tool to invent a card shape and any
 * renderer to invent a destructure — the two sides only "agree" because the
 * frontend `tool-renderer.tsx` and each `*.tool.ts` file happen to spell the
 * same fields the same way in prose. That is the drift class this file closes.
 *
 * `CardEnvelope` is the discriminated union of EVERY presentation `type` the
 * factory or the tools currently emit. Every schema below was reconciled to a
 * REAL emitter site — no invented fields, no fields that only the renderer
 * knows how to spell.
 *
 * Phase 0 ships this union ALONGSIDE the existing `PresentationPayload`
 * (which keeps its escape hatch — Phase 0 is additive-only). Phase 3 removes
 * the escape hatch and replaces `PresentationPayload` with `CardEnvelope`,
 * after per-area migrations validate every emitter's output through the
 * matching card schema.
 *
 * See `docs/claire/foundation.md` for the phased migration plan.
 */
// ─────────────────────────────────────────────────────────────────────────────
// Shared sub-shapes
// ─────────────────────────────────────────────────────────────────────────────
const summaryFieldSchema = z.object({
    label: z.string(),
    value: z.string(),
});
// The ClaireConfirmationAction enum lives in @borradh-workspace/database; the
// factory writes it into token rows. Keep this as `z.string()` here — the DB
// enum is the source of truth and pulling it in would drag database types
// into this pure-schema file. Phase 3's per-tool validation binds tighter.
const claireConfirmationActionSchema = z.string();
// ─────────────────────────────────────────────────────────────────────────────
// 1. confirmation_required — factory-emitted when a destructive tool first runs
// ─────────────────────────────────────────────────────────────────────────────
export const confirmationRequiredSchema = z.object({
    type: z.literal('confirmation_required'),
    action: claireConfirmationActionSchema,
    resourceId: z.string(),
    token: z.string(),
    expiresAt: z.string(),
    summary: z
        .object({
        title: z.string().optional(),
        fields: z.array(summaryFieldSchema).optional(),
        campaignPackage: campaignPackageReviewSchema.optional(),
    })
        .optional(),
    executeToolName: z.string(),
    renderer: z.string().optional(),
    // Optional custom label for the confirm button in the FE
    // `ConfirmationRequiredPresentation` card. Phase 1 flagged this: the FE
    // reads `presentation.confirmLabel ?? 'Confirm'`, but the shared schema
    // did not declare it, so the field would be stripped by a strict parse.
    confirmLabel: z.string().optional(),
    // Set when the factory auto-re-staged a fresh card because the previous
    // token had expired (or was unknown) — a dead end otherwise: the operator
    // said "yes, go ahead" twice and got escalated. The FE renders it as a
    // small explanatory line above the summary.
    restagedNote: z.string().optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// 2. confirmation_expired — factory-emitted on failed token verification
// ─────────────────────────────────────────────────────────────────────────────
export const confirmationExpiredSchema = z.object({
    type: z.literal('confirmation_expired'),
    reason: z.enum([
        'expired',
        'consumed',
        'mismatch',
        'not_found',
        'no_user_turn',
        // The owner pressed Cancel on the card (`declineApprovalCard`).
        'declined',
        // A token that recorded NO payload cannot prove what a human approved, so
        // the execute half refuses rather than trusting the model's argument.
        // See `tool-factory/confirmed-payload.ts`.
        'no_payload',
    ]),
});
// ─────────────────────────────────────────────────────────────────────────────
// 3. hard_block_violation — factory-emitted when a hard-block validator fails
// ─────────────────────────────────────────────────────────────────────────────
export const hardBlockViolationSchema = z.object({
    type: z.literal('hard_block_violation'),
    code: z.string(),
    message: z.string(),
});
// ─────────────────────────────────────────────────────────────────────────────
// 4. graphic_draft — emitted by content_createContent when a graphic proposal
//    is created but not yet generated. The card owns source-image selection.
// ─────────────────────────────────────────────────────────────────────────────
export const graphicDraftSchema = z.object({
    type: z.literal('graphic_draft'),
    imageMode: z.enum(['generate', 'assemble']).optional(),
    itemId: z.string().optional(),
    attemptId: z.string().optional(),
    serviceId: z.string(),
    category: z.string(),
    imageryPolicy: z.enum(graphicImageryPolicyValues).optional(),
    includeLogo: z.boolean().optional(),
    logoPlacement: graphicLogoPlacementSchema.optional(),
    kind: z.string().optional(),
    style: z.string().optional(),
    slideCount: z.number().optional(),
    assetRef: z.string().optional(),
    candidateAssetIds: z.array(z.string()).optional(),
    sourceAssetIds: z.array(z.string()).optional(),
    topicSummary: z.string().optional(),
    /**
     * Every visual requirement the owner stated (border, exact text, added
     * elements…), combined by Claire into one brief. Accept forwards it as the
     * render's `refinementInstruction`, so one graphic carries all of them.
     */
    refinementInstruction: z.string().optional(),
    title: z.string().optional(),
    fields: z.array(summaryFieldSchema).optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// 5. graphic_status — emitted by content_createContent + content_patchContent
//    when a graphic is rendering. The card polls until the image lands.
// ─────────────────────────────────────────────────────────────────────────────
export const graphicStatusSchema = z.object({
    type: z.literal('graphic_status'),
    graphicId: z.string(),
    itemId: z.string().optional(),
    // Stamps which content_createContent/content_patchContent CALL this card
    // came from. Without it, every graphic_status card for the same itemId
    // across the transcript reads the SAME query-cache entry
    // (queryKeys.contentBatches.itemState defaults to 'current' when absent),
    // so a redo on the newest card updates every older card for that item too.
    attemptId: z.string().optional(),
    status: z.string().optional(),
    title: z.string().optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// 6. video_draft — DEGRADED path from content_createContent when the content
//    item couldn't be opened. Carries the raw draft rather than losing it.
// ─────────────────────────────────────────────────────────────────────────────
export const videoDraftSchema = z.object({
    type: z.literal('video_draft'),
    videoId: z.string(),
    title: z.string().optional(),
    fields: z.array(summaryFieldSchema).optional(),
    serviceId: z.string().nullable().optional(),
    clipAssetIds: z.array(z.string()).optional(),
    minClipCount: z.number().optional(),
    textFrames: z
        .array(z.object({
        id: z.string(),
        text: z.string(),
        style: z
            .enum(['default', 'question', 'answer', 'disclaimer', 'cta'])
            .optional(),
    }))
        .optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// 7. video_status — emitted by videos_getVideoStatus, content_renderVideo,
//    and content_createContent. Ready-state carries the media URL; running
//    states carry progress. The card polls internally and swaps to the video
//    thumbnail when ready.
// ─────────────────────────────────────────────────────────────────────────────
export const videoStatusSchema = z.object({
    type: z.literal('video_status'),
    videoId: z.string(),
    status: z.string().optional(),
    progress: z.number().optional(),
    processingStage: z.string().optional(),
    title: z.string().optional(),
    blobUrl: z.string().optional(),
    thumbnailUrl: z.string().optional(),
    durationMs: z.number().optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// 8. content_clips — emitted by content_createContent + content_patchContent
//    for a video item. Addresses the CONTENT ITEM (not the video) so the fork
//    lineage is preserved across edits.
// ─────────────────────────────────────────────────────────────────────────────
/** No draft exists yet. The owner can pick sources and retry the same request. */
export const videoFootageRequiredSchema = z.object({
    type: z.literal('video_footage_required'),
    title: z.string().optional(),
    selectedAssetIds: z.array(z.string()).optional(),
    message: z.string(),
    request: z.object({
        serviceId: z.string(),
        format: z.string().optional(),
        templateId: z.string().optional(),
        offerId: z.string().optional(),
        title: z.string().optional(),
        topicSummary: z.string().optional(),
        usage: z.enum(['organic', 'ad']).optional(),
        musicTrackId: z.string().optional(),
        musicVolume: z.number().optional(),
        offerCopy: z
            .object({
            headline: z.string().optional(),
            bulletPoints: z.array(z.string()).optional(),
            ctaText: z.string().optional(),
            urgencyText: z.string().optional(),
        })
            .optional(),
    }),
});
/** Only the chosen IDs cross the transport; request settings come from the saved card. */
export const videoFootageSelectionSchema = z.object({
    toolCallId: z.string().min(1).max(128),
    assetIds: z.array(z.string().min(1).max(128)).min(1).max(10),
});
export const contentClipsSchema = z.object({
    type: z.literal('content_clips'),
    itemId: z.string(),
    attemptId: z.string().optional(),
    renderCount: z.number().optional(),
    title: z.string().optional(),
    serviceId: z.string().nullable().optional(),
    minClipCount: z.number().optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// 9. ad_preview — emitted by ads_createDraftAd / ads_executeLaunchAd /
//    ads_updateAd / ads_replaceAdCreative.
//
//    Phase 3 collapse: the rich creative/copy fields now live on
//    `presentation.preview` (mirroring `AdPreviewProps` in the FE renderer).
//    They previously ALSO lived on `data.preview`; the FE dispatch reads the
//    envelope now, and the emitters no longer duplicate the shape on `data`.
// ─────────────────────────────────────────────────────────────────────────────
const adLaunchCardStateSchema = z.enum([
    'live',
    'live_but_campaign_paused',
    'pending_review',
    'paused_at_meta',
    'rejected',
    'failed',
    'unverified',
    'already_live',
]);
export const adPreviewPreviewSchema = z.object({
    variant: z.enum(['draft', 'launched']),
    context: z.enum(['launch', 'edit']).optional(),
    launchState: adLaunchCardStateSchema.optional(),
    adName: z.string(),
    headline: z.string().optional(),
    primaryText: z.string().optional(),
    description: z.string().optional(),
    callToAction: z.string().optional(),
    campaignName: z.string().optional(),
    videoTitle: z.string().optional(),
    videoId: z.string().optional(),
    graphicId: z.string().optional(),
    assetImageUrl: z.string().optional(),
    assetVideoUrl: z.string().optional(),
    assetThumbnailUrl: z.string().optional(),
    budgetDisplay: z.string().optional(),
    targetingDisplay: z.string().optional(),
    destinationUrl: z.string().optional(),
    details: z
        .array(z.object({
        title: z.string(),
        fields: z.array(z.object({ label: z.string(), value: z.string() })),
    }))
        .optional(),
});
export const adPreviewSchema = z.object({
    type: z.literal('ad_preview'),
    // Phase 4A: `preview` is REQUIRED on the envelope. The four emitters
    // (createDraftAd / executeLaunchAd / updateAd / replaceAdCreative) now
    // attach the AdPreviewCard payload here rather than on `data.preview`, so
    // the envelope carries the render payload and the FE reads it via
    // `envelope.preview` instead of `output.preview`.
    preview: adPreviewPreviewSchema,
});
// ─────────────────────────────────────────────────────────────────────────────
// 10. campaign_preview — emitted by campaigns_showCampaignPreview. Discriminator
//     plus campaignId; the rich preview payload lives on `data` (typed as
//     `CampaignPreviewData` in the frontend renderer).
// ─────────────────────────────────────────────────────────────────────────────
export const campaignPreviewSchema = z.object({
    type: z.literal('campaign_preview'),
    campaignId: z.string(),
});
// ─────────────────────────────────────────────────────────────────────────────
// 11. qr_code — emitted by videos_generateTalkingHeadQR. Recording URL and
//     scan instructions for the talking-head capture flow.
// ─────────────────────────────────────────────────────────────────────────────
export const qrCodeSchema = z.object({
    type: z.literal('qr_code'),
    recordingUrl: z.string(),
    instructions: z.string().optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// 12. asset_picker — emitted by content_listMedia. Presents a grid the owner
//     picks from by eye.
// ─────────────────────────────────────────────────────────────────────────────
export const assetPickerAssetSchema = z.object({
    id: z.string(),
    name: z.string(),
    type: z.string().optional(),
    duration: z.union([z.number(), z.string(), z.null()]).optional(),
    blobUrl: z.string().nullable().optional(),
    thumbnailUrl: z.string().nullable().optional(),
    tags: z.array(z.string()).nullable().optional(),
});
export const assetPickerSchema = z.object({
    type: z.literal('asset_picker'),
    requiresSelection: z.boolean().optional(),
    assets: z.array(assetPickerAssetSchema),
    total: z.number().optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// 13. draft_reply — emitted by customerConversations_draftReply. The drafted
//     reply the operator holds before sending.
// ─────────────────────────────────────────────────────────────────────────────
export const draftReplySchema = z.object({
    type: z.literal('draft_reply'),
    conversationId: z.string(),
    draft: z.string(),
    customerName: z.string().nullable().optional(),
    platform: z.string().optional(),
    suggestedSendAction: z.literal('send_message').optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// 14. support_chat — emitted by support_requestSupportChat. Whether an Intercom
//     hand-off was opened, and whether Intercom is configured at all.
// ─────────────────────────────────────────────────────────────────────────────
export const supportChatSchema = z.object({
    type: z.literal('support_chat'),
    reason: z.string().optional(),
    created: z.boolean().optional(),
    notConfigured: z.boolean().optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// 15. stop_and_ask — factory-attached by the per-turn circuit breaker
//     (`failure-tracker.ts`) after a tool has failed identically N times.
// ─────────────────────────────────────────────────────────────────────────────
export const stopAndAskSchema = z.object({
    type: z.literal('stop_and_ask'),
    toolName: z.string().optional(),
    failureCount: z.number().optional(),
    reason: z.string().optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// 16. existing_candidates — emitted by ads_createCampaign / lead-forms_
//     createLeadForm when a recent, similar resource exists. Owner picks
//     "reuse this" or "create anyway".
//
//     ⚠️  DUAL SHAPE — Phase 2 documented (not fixed).
//     Both emitters currently return TWO parallel copies of this card:
//       1. `presentation: { type: 'existing_candidates', kind, proposedName,
//          candidates }` — matches this leaf schema.
//       2. Top-level `data: { uiState: 'existing_candidates', proposedName,
//          existingCandidates, message, title }` — what the frontend
//          `tool-renderer.tsx` generic dispatch actually reads (case in the
//          envelope switch falls through to `break`; the render happens off
//          `output.uiState`).
//     The two shapes name the array differently (`candidates` vs
//     `existingCandidates`). Phase 3 should collapse this to the presentation
//     shape and move the FE dispatch into the envelope switch — see the
//     `lead-forms-tools.spec.ts` "existing_candidates envelope" describe
//     block which currently pins BOTH shapes. Kept dual here so the
//     `outputSchema`s on the two emitting tools can pass without a
//     cross-area behaviour change (createCampaign lives under `ads/`, out of
//     scope for the leads/lead-forms Phase 2A window).
// ─────────────────────────────────────────────────────────────────────────────
export const existingCandidateSchema = z.object({
    id: z.string(),
    name: z.string(),
    createdAt: z.string().optional(),
    matchReasons: z.array(z.string()).optional(),
});
export const existingCandidatesSchema = z.object({
    type: z.literal('existing_candidates'),
    kind: z.enum(['campaign', 'lead_form']),
    proposedName: z.string().optional(),
    candidates: z.array(existingCandidateSchema),
});
// ─────────────────────────────────────────────────────────────────────────────
// 17. preview_card — emitted by claire_showAdPreview / claire_showOfferPreview.
//     The `state` shape depends on `kind` (`ad` vs `offer`); it is the snapshot
//     the Window-7 preview card renders and re-fetches on edit.
//
//     Phase 2A (offers) tightens the `offer` branch's `state` to a real
//     z.object matching `DraftOfferSnapshot` (the shape `offerToSnapshot`
//     emits — 1:1 with the frontend `OfferPreviewCardState`). The `ad`
//     branch stays opaque until the ads Phase 2 migration lands; Phase 3
//     replaces the `z.record` there with `adPreviewCardStateSchema`.
// ─────────────────────────────────────────────────────────────────────────────
/**
 * The offer draft snapshot carried on a `preview_card` with `kind: 'offer'`.
 * Mirrors `apps/api/src/assistant/tools/claire/types.ts#DraftOfferSnapshot`
 * and the frontend `OfferPreviewCardState`. Fields kept loose here (plain
 * `z.string()` for the offer's own `state` and `discountType`) rather than
 * pulling `offerStateValues` / `offerDiscountTypeValues` from
 * `@borradh-workspace/labels` — the labels package is a runtime dep on
 * `packages/features`, and this file is imported by both frontends and the
 * API. The DB enum stays the source of truth; the emitter is what binds the
 * value, not this schema.
 */
export const offerPreviewCardStateSchema = z.object({
    draftId: z.string(),
    name: z.string(),
    code: z.string().nullable(),
    state: z.string(),
    discountType: z.string(),
    discountPercent: z.number().nullable(),
    offerPriceCents: z.number().nullable(),
    originalPriceCents: z.number().nullable(),
    buyQuantity: z.number().nullable(),
    getQuantity: z.number().nullable(),
    limitPerClient: z.boolean(),
    redemptionLimit: z.number().nullable(),
    validFrom: z.string().nullable(),
    validUntil: z.string().nullable(),
    serviceIds: z.array(z.string()),
    locationIds: z.array(z.string()),
});
/**
 * The ad draft snapshot carried on a `preview_card` with `kind: 'ad'`.
 * Mirrors `apps/app/src/features/claire/chat-preview/ad-preview-card.tsx#AdPreviewCardState`.
 * Phase 4A: replaces the opaque `z.record()` placeholder so the ad variant
 * matches its offer sibling.
 */
export const adPreviewCardStateSchema = z.object({
    draftId: z.string(),
    name: z.string(),
    headline: z.string().nullable(),
    primaryText: z.string().nullable(),
    videoId: z.string().nullable(),
});
export const previewCardSchema = z.discriminatedUnion('kind', [
    z.object({
        type: z.literal('preview_card'),
        kind: z.literal('offer'),
        draftId: z.string(),
        state: offerPreviewCardStateSchema,
    }),
    z.object({
        type: z.literal('preview_card'),
        kind: z.literal('ad'),
        draftId: z.string(),
        state: adPreviewCardStateSchema,
    }),
]);
// ─────────────────────────────────────────────────────────────────────────────
// 18. open_in_app — emitted by website_openDesigner. The whole card is a
//     deep-link out of chat, or the website designer's approval card when it
//     carries a `designerRequest`.
//
//     ⚠️  Envelope-kind name collision: `claire-eval` defines
//     `InteractivePayload.kind: 'open_in_app'` under `ClairePayload.interactive`.
//     That is a different discriminator (payload shape, not card shape) — the
//     two never meet on the wire. Kept name-matched deliberately so a reader
//     scanning for "open_in_app" finds both, and the invariant "this card is a
//     link out" reads the same on both sides.
// ─────────────────────────────────────────────────────────────────────────────
export const openInAppSchema = z.object({
    type: z.literal('open_in_app'),
    url: z.string(),
    label: z.string(),
    designerRequest: z
        .object({
        organizationId: z.string().min(1),
        locationId: z.string().min(1),
        request: z.string().trim().min(1).max(8000),
        locationName: z.string().optional(),
    })
        .optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// 19. none — the explicit "render nothing" sentinel. Used by content_
//     createContent / content_patchContent when the tool was asked to suppress
//     its own card (e.g. an offer video created as the creative for a draft
//     ad, whose combined card shows later in the turn). The FE renderer
//     early-returns null.
// ─────────────────────────────────────────────────────────────────────────────
export const noneSchema = z.object({
    type: z.literal('none'),
});
// ─────────────────────────────────────────────────────────────────────────────
// 20. tour_dispatch — emitted by meta_dispatchTour. Fires the frontend's
//     ClaireWalkthroughProvider for the named tour after navigating to the
//     tour's target route.
// ─────────────────────────────────────────────────────────────────────────────
export const tourDispatchSchema = z.object({
    type: z.literal('tour_dispatch'),
    tourKind: z.string(),
    payload: z.record(z.string(), z.unknown()),
    navigateTo: z.string(),
});
// ─────────────────────────────────────────────────────────────────────────────
// 21. memory_saved — emitted by meta_remember when a preference or fact was
//     written. The card shows the acknowledged text and the effective scope.
// ─────────────────────────────────────────────────────────────────────────────
export const memorySavedSchema = z.object({
    type: z.literal('memory_saved'),
    scope: z.enum(['personal', 'organization']),
    content: z.string(),
    expiresAt: z.string().optional(),
    /** Where it applies, e.g. "Graphics" or "Everywhere". */
    appliesTo: z.string().optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// 21b. memory_removed / memory_updated / memory_list — the rest of what the
//      memory tools do. Every one used to end as a sentence in the transcript,
//      so the owner could not see WHAT was removed, switched off, or saved
//      (Claire rewords a memory as she stores it).
// ─────────────────────────────────────────────────────────────────────────────
export const memoryRemovedSchema = z.object({
    type: z.literal('memory_removed'),
    content: z.string(),
    scope: z.enum(['personal', 'organization']),
    appliesTo: z.string().optional(),
});
export const memoryUpdatedSchema = z.object({
    type: z.literal('memory_updated'),
    content: z.string(),
    enabled: z.boolean(),
    scope: z.enum(['personal', 'organization']),
    appliesTo: z.string().optional(),
});
export const memoryListSchema = z.object({
    type: z.literal('memory_list'),
    memories: z.array(z.object({
        knowledgeEntryId: z.string(),
        content: z.string(),
        scope: z.enum(['personal', 'team']),
        appliesTo: z.string(),
        enabled: z.boolean(),
        expiresAt: z.string().nullable().optional(),
    })),
});
// ─────────────────────────────────────────────────────────────────────────────
// 22. memory_suggestion — emitted by meta_suggestMemory (Phase 1,
//     docs/plans/claire-memory-suggestions.md). Claire noticed a durable-
//     looking preference and offers to save it; nothing is written yet. The
//     card carries Save / Dismiss actions — Save sends a follow-up chat
//     message that becomes a normal `meta_remember` call, Dismiss sends
//     nothing back to the API in Phase 1.
// ─────────────────────────────────────────────────────────────────────────────
export const memorySuggestionSchema = z.object({
    type: z.literal('memory_suggestion'),
    content: z.string(),
    /** Skill id(s) this suggestion is scoped to. Absent = global. */
    scopeSkillIds: z.array(z.string()).optional(),
    /** Human-readable scope for the card, e.g. "graphics" or "everything Claire creates". */
    scopeLabel: z.string(),
    /**
     * Phase 3 (docs/plans/claire-memory-suggestions.md) — the conflict-UI
     * first pass. Set when the proposed content is a near-repeat of an
     * EXISTING memory in the same scope; the card then offers "Replace it"
     * (delete the old one, save the new one) alongside the usual Save /
     * Dismiss, instead of silently letting near-duplicate rows accumulate.
     */
    conflictsWith: z
        .object({
        knowledgeEntryId: z.string(),
        content: z.string(),
    })
        .optional(),
    /**
     * Web only: the suggestion is NOT part of the chat. The app lifts it out of
     * the transcript into a card beside the chat, and the owner answers it
     * there straight against the API
     * (`POST /assistant/memory-proposals/:token/save|dismiss`) — no chat
     * message, and Claire is told to carry on without it. Absent on WhatsApp,
     * where the card is a message with reply buttons like before.
     */
    proposal: z
        .object({
        token: z.string(),
        /** When the suggestion can no longer be answered. */
        expiresAt: z.string(),
        /** `asked`: the owner said "remember…"; `noticed`: Claire spotted it. */
        origin: z.enum(['asked', 'noticed']),
        scope: z.enum(['personal', 'organization']),
        /** When the MEMORY stops applying, if it is time-bound. */
        memoryExpiresAt: z.string().optional(),
    })
        .optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// Asset reference — an ambiguous owner-named upload, rendered as a chooser.
// ─────────────────────────────────────────────────────────────────────────────
export const assetReferenceSchema = z.object({
    type: z.literal('asset_reference'),
    assetRef: z.string(),
    candidates: z.array(z.object({
        id: z.string(),
        name: z.string(),
        thumbnailUrl: z.string().nullable().optional(),
        blobUrl: z.string().nullable().optional(),
    })),
});
// ─────────────────────────────────────────────────────────────────────────────
// The discriminated union.
// ─────────────────────────────────────────────────────────────────────────────
export const cardEnvelopeSchema = z.discriminatedUnion('type', [
    confirmationRequiredSchema,
    confirmationExpiredSchema,
    hardBlockViolationSchema,
    graphicDraftSchema,
    graphicStatusSchema,
    videoDraftSchema,
    videoStatusSchema,
    contentClipsSchema,
    videoFootageRequiredSchema,
    adPreviewSchema,
    campaignPreviewSchema,
    qrCodeSchema,
    assetPickerSchema,
    draftReplySchema,
    supportChatSchema,
    stopAndAskSchema,
    existingCandidatesSchema,
    previewCardSchema,
    openInAppSchema,
    noneSchema,
    tourDispatchSchema,
    memorySavedSchema,
    memoryRemovedSchema,
    memoryUpdatedSchema,
    memoryListSchema,
    memorySuggestionSchema,
    assetReferenceSchema,
]);
//# sourceMappingURL=card-envelope.js.map